import { IWorkFlowBaseInfo } from "@kuika/kuika-cl-model";
import * as React from "react";
import { getProcessUsedActionIds } from "../../shared/utility/process-action-usage";
import { NoticeBanner } from "./notice-banner";

export interface IActionUsedInProcessAlertProps {
  /** The action-definition id being edited. When it resolves to an action used in a process, the banner shows. */
  actionId: string | undefined;
  workFlowList: IWorkFlowBaseInfo[] | undefined;
  projectId: string | undefined;
}

interface IActionUsedInProcessAlertState {
  isUsedInProcess: boolean;
  isDismissed: boolean;
}

// Shared banner warning that the edited action is used in a process (KOA-18085). Mounted in the SQL /
// generic-sql modals; renders null until detection confirms usage. Detection: getProcessUsedActionIds.
export class ActionUsedInProcessAlert extends React.PureComponent<
  IActionUsedInProcessAlertProps,
  IActionUsedInProcessAlertState
> {
  private isMounted_: boolean = false;

  constructor(props: IActionUsedInProcessAlertProps) {
    super(props);
    this.state = { isUsedInProcess: false, isDismissed: false };
  }

  componentDidMount(): void {
    this.isMounted_ = true;
    this.checkUsage();
  }

  componentDidUpdate(prevProps: IActionUsedInProcessAlertProps): void {
    if (prevProps.actionId !== this.props.actionId) {
      // A different action is a different warning: an earlier dismissal must not hide it.
      this.setState({ isDismissed: false });
      this.checkUsage();
    }
  }

  componentWillUnmount(): void {
    // Detection is async; guard setState if the editor closes mid-flight.
    this.isMounted_ = false;
  }

  checkUsage = async (): Promise<void> => {
    const actionId: string | undefined = this.props.actionId;
    if (!actionId) {
      if (this.isMounted_) {
        this.setState({ isUsedInProcess: false });
      }
      return;
    }
    const usedActionIds: Set<string> = await getProcessUsedActionIds(this.props.workFlowList, this.props.projectId);
    if (this.isMounted_) {
      this.setState({ isUsedInProcess: usedActionIds.has(actionId.toLowerCase()) });
    }
  };

  render(): React.ReactNode {
    if (!this.state.isUsedInProcess || this.state.isDismissed) {
      return null;
    }
    return (
      <NoticeBanner
        severity="warning"
        badge="IN USE"
        title="Action is used in a process"
        description="Making changes to this action may affect older versions of that process. If you need to change the action, please consider creating a new one."
        onClose={() => this.setState({ isDismissed: true })}
        flush
      />
    );
  }
}
